import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "@studio-freight/lenis"; // Parcel handles the rest!

gsap.registerPlugin(ScrollTrigger);

// ———————————————————————————
// LENIS SCROLL
// ———————————————————————————
const lenis = new Lenis({
  duration: 1.2, // Scroll duration
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Easing function for smooth transitions
  direction: "vertical", // Scroll direction: vertical or horizontal
  gestureDirection: "vertical", // Gesture direction: vertical, horizontal, or both
  smooth: true, // Enable smooth scrolling
  mouseMultiplier: 1, // Mouse scroll speed multiplier
  smoothTouch: false, // Disable smooth scrolling for touch devices
  touchMultiplier: 2, // Touch scroll speed multiplier
  infinite: false, // Disable infinite scroll
});

// Optional: Listen to scroll events
lenis.on("scroll", ({ scroll, limit, velocity, direction, progress }) => {
  console.log({ scroll, limit, velocity, direction, progress });
});

// Keep ONLY this for the loop
gsap.ticker.add((time) => {
  lenis.raf(time * 1000);
});
gsap.ticker.lagSmoothing(0);

// ———————————————————————————
// SPLASH SCREEN ANIMATION
// ———————————————————————————
const splashScreen = document.querySelector(".splashscreen-container");
const leftText = document.querySelector(".splashscreen-text.left");
const rightText = document.querySelector(".splashscreen-text.right");
const images = document.querySelectorAll(".img-mask img");
const splashImg = document.querySelector(".splashscreen-img");

// Updated Lock/Unlock for Lenis compatibility
const lockScroll = () => {
  lenis.stop();
  document.documentElement.classList.add("no-scroll");
  document.body.classList.add("no-scroll");
};

const unlockScroll = () => {
  lenis.start();
  document.documentElement.classList.remove("no-scroll");
  document.body.classList.remove("no-scroll");
};

if (sessionStorage.getItem("splashSeen")) {
  if (splashScreen) splashScreen.style.display = "none";
  unlockScroll();
} else {
  lockScroll();

  window.addEventListener("load", () => {
    // Measure text offsets relative to the image
    const imgRect = splashImg.getBoundingClientRect();
    const leftTextRect = leftText.getBoundingClientRect();
    const rightTextRect = rightText.getBoundingClientRect();

    const leftOffset = imgRect.left - leftTextRect.right;
    let rightOffset = imgRect.right - rightTextRect.left;

    const isFirefox = typeof InstallTrigger !== "undefined";
    if (isFirefox) rightOffset -= 100;

    gsap
      .timeline({ defaults: { ease: "power2.in" } })
      .to([leftText, rightText], { opacity: 1, duration: 0 })
      .to(leftText, { x: leftOffset + 100, duration: 0.8 }, ">0.2")
      .to(rightText, { x: rightOffset - 100, duration: 0.8 }, "<")
      .to(images, { y: 0, duration: 0.9, stagger: 0.7 }, ">0")
      .to({}, { duration: 0.2 })
      .to(splashScreen, {
        opacity: 0,
        duration: 0.6,
        onStart: () => (splashScreen.style.pointerEvents = "none"),
        onComplete: () => {
          unlockScroll();
          sessionStorage.setItem("splashSeen", "true");
        },
      });
  });
}

// ———————————————————————————
// WARNING SMALL SCREENS
// ———————————————————————————
const sizeWarning = document.querySelector(".size-warning-container");
const closeWarningBtn = document.querySelector(".js-close-warning");

const hideWarning = () => {
  if (sizeWarning) {
    gsap.to(sizeWarning, {
      opacity: 0,
      duration: 0.5,
      onComplete: () => {
        sizeWarning.style.display = "none";
        // After hiding the warning, make sure Lenis is allowed to start
        if (typeof lenis !== "undefined") lenis.start();
      },
    });
  }
};

// Check if they already clicked "still want to see it" this session
if (sessionStorage.getItem("sizeWarningDismissed")) {
  if (sizeWarning) sizeWarning.style.display = "none";
} else {
  // If not dismissed, and the button is clicked:
  if (closeWarningBtn) {
    closeWarningBtn.addEventListener("click", (e) => {
      e.preventDefault();
      sessionStorage.setItem("sizeWarningDismissed", "true");
      hideWarning();
    });
  }
}

// ———————————————————————————
// CUSTOM CURSOR
// ———————————————————————————
const cursor = document.querySelector(".custom-cursor");
window.addEventListener("mousemove", (e) =>
  gsap.set(cursor, { x: e.clientX, y: e.clientY }),
);

const cursorIn = () =>
  gsap.to(cursor, {
    scale: 0.7,
    backgroundColor: "#fff",
    duration: 0.1,
    ease: "power1.in",
  });
const cursorOut = () =>
  gsap.to(cursor, {
    scale: 1,
    backgroundColor: "transparent",
    duration: 0.1,
    ease: "power1.out",
  });

document.querySelectorAll("a, button").forEach((el) => {
  el.addEventListener("mouseenter", cursorIn);
  el.addEventListener("mouseleave", cursorOut);
});
document.addEventListener(
  "pointerdown",
  (e) => e.target.closest("a, button") && cursorIn(),
);
document.addEventListener("pointerup", cursorOut);
document.addEventListener("pointercancel", cursorOut);

// ———————————————————————————
// SECTION BRANCHES LINK HOVER ANIMATION
// ———————————————————————————
document.querySelectorAll(".section-branches a").forEach((link) => {
  const bg = link.querySelector(".bg");

  const slideIn = (e) => {
    const rect = link.getBoundingClientRect();
    // Calculate position relative to the element, not the whole screen
    const fromTop = e.clientY - rect.top < rect.height / 2;

    gsap.fromTo(
      bg,
      { y: fromTop ? "-100%" : "100%" },
      {
        y: "0%",
        duration: 0.1,
        ease: "power2.out",
        overwrite: "auto", // Prevents animation overlapping
      },
    );
  };

  const slideOut = (e) => {
    const rect = link.getBoundingClientRect();
    const toTop = e.clientY - rect.top < rect.height / 2;

    gsap.to(bg, {
      y: toTop ? "-100%" : "100%",
      duration: 0.1,
      ease: "power2.in",
      overwrite: "auto", // Forces the exit animation to take priority
    });
  };

  link.addEventListener("mouseenter", slideIn);
  link.addEventListener("mouseleave", slideOut);
});

// ———————————————————————————
// CAROUSEL
// ———————————————————————————
const initCarousel = () => {
  document.querySelectorAll(".selected-carousel-row").forEach((row, index) => {
    row.innerHTML += row.innerHTML;

    const setup = () => {
      const rowWidth = row.scrollWidth / 2;
      if (!rowWidth) {
        requestAnimationFrame(setup);
        return;
      }

      if (index === 1) gsap.set(row, { x: -rowWidth });
      gsap.to(row, {
        x: index === 1 ? 0 : -rowWidth,
        duration: 30,
        ease: "none",
        repeat: -1,
        overwrite: true,
      });
    };

    requestAnimationFrame(setup);
  });
};

window.addEventListener("load", initCarousel);

// ———————————————————————————
// SNAPPING
// ———————————————————————————
// 1. Sync ScrollTrigger with Lenis
lenis.on("scroll", ScrollTrigger.update);

const snappingSections = gsap.utils.toArray(".snapping");

// Your existing logic for "crossing" sections
snappingSections.forEach((section) => {
  ScrollTrigger.create({
    trigger: section,
    start: "top 40%",
    end: "bottom 60%",
    onEnter: () => lenis.scrollTo(section, { duration: 1, lerp: 0.1 }),
    onEnterBack: () => lenis.scrollTo(section, { duration: 1, lerp: 0.1 }),
  });
});

// 2. The "Realignment" Logic (Mandatory Snap)
// This fires when the user stops interacting with the scroll
lenis.on("scroll", () => {
  clearTimeout(window.snapTimeout);
  window.snapTimeout = setTimeout(() => {
    // Find which section is currently closest to the top of the screen
    let closestSection = snappingSections[0];
    let minDistance = Math.abs(snappingSections[0].getBoundingClientRect().top);

    snappingSections.forEach((section) => {
      const distance = Math.abs(section.getBoundingClientRect().top);
      if (distance < minDistance) {
        minDistance = distance;
        closestSection = section;
      }
    });

    // If we aren't perfectly aligned, snap to the closest one
    if (minDistance > 5) {
      // 5px threshold to prevent infinite loops
      lenis.scrollTo(closestSection, { duration: 0.8, lerp: 0.1 });
    }
  }, 150); // 150ms delay after scrolling stops
});

// ———————————————————————————
// SWIPE IMAGE
// ———————————————————————————
gsap.set(".swipeimage-container img.swipeimage", {
  yPercent: -50,
  xPercent: -50,
});

let activeImage;
gsap.utils.toArray(".swipeimage-container").forEach((el) => {
  let image = el.querySelector("img.swipeimage"),
    setX,
    setY,
    align = (e) => {
      setX(e.clientX);
      setY(e.clientY);
    },
    startFollow = () => document.addEventListener("mousemove", align),
    stopFollow = () => document.removeEventListener("mousemove", align),
    fade = gsap.to(image, {
      autoAlpha: 1,
      ease: "none",
      paused: true,
      onReverseComplete: stopFollow,
    });

  el.addEventListener("mouseenter", (e) => {
    fade.play();
    startFollow();

    if (activeImage) {
      // match position of currently active image
      gsap.set(image, {
        x: gsap.getProperty(activeImage, "x"),
        y: gsap.getProperty(activeImage, "y"),
      });
    }

    activeImage = image;

    // SLOWER follow for smooth trailing
    setX = gsap.quickTo(image, "x", { duration: 0.7, ease: "power2.out" });
    setY = gsap.quickTo(image, "y", { duration: 0.7, ease: "power2.out" });

    align(e);
  });

  el.addEventListener("mouseleave", () => fade.reverse());
});

// ———————————————————————————
// INFO
// ———————————————————————————
const infoButton = document.querySelector(
  ".photobook-nav button:nth-child(2) a",
);
const infoPanel = document.querySelector(".photobook-info");

if (infoButton && infoPanel) {
  const infoCloseButton = infoPanel.querySelector("button a");
  gsap.set(infoPanel, { x: "100%" });

  infoButton.addEventListener("click", (e) => {
    e.preventDefault();
    gsap.to(infoPanel, { x: "0%", duration: 0.6, ease: "power3.out" });
  });

  infoCloseButton.addEventListener("click", (e) => {
    e.preventDefault();
    gsap.to(infoPanel, { x: "100%", duration: 0.6, ease: "power3.in" });
  });
}

// ———————————————————————————
// PHOTOBOOK SCROLL ANIMATION
// ———————————————————————————

window.addEventListener("load", () => {
  // Select every column on the page
  const columns = document.querySelectorAll(".photobook-column");

  columns.forEach((column) => {
    // Find the specific container this column belongs to
    const container = column.closest(".photobook-container");

    if (container) {
      gsap.to(column, {
        // Distance is: (Container Height) - (This Column's Height)
        y: () => container.offsetHeight - column.offsetHeight,
        ease: "none",
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: "bottom bottom",
          scrub: true,
          invalidateOnRefresh: true, // Recalculates if user resizes window
        },
      });
    }
  });
});

// ———————————————————————————
// LIGHTBOX CUSTOM (PhotoSwipe pour le routing, custom pour l'UI)
// ———————————————————————————
const initLightbox = () => {
  const triggers = [...document.querySelectorAll(".pswp-trigger")];
  if (!triggers.length) return;

  // Construire la galerie depuis les img.swipeimage (src géré par Parcel)
  const gallery = triggers
    .map((el) => {
      const container = el.closest(".swipeimage-container");
      const img = container ? container.querySelector("img.swipeimage") : null;
      return img ? img.src : "";
    })
    .filter(Boolean);

  let currentIndex = 0;

  // Créer l'overlay
  const overlay = document.createElement("div");
  overlay.id = "custom-lightbox";
  overlay.innerHTML = `<img class="lb-img" src="" alt="" />`;
  document.body.appendChild(overlay);

  const lbImg = overlay.querySelector(".lb-img");

  const showImage = (index) => {
    lbImg.style.opacity = "0";
    lbImg.src = gallery[index];
    lbImg.onload = () => {
      lbImg.style.opacity = "1";
    };
  };

  const open = (index) => {
    currentIndex = index;
    showImage(currentIndex);
    overlay.classList.add("is-open");
    lockScroll();
  };

  const close = () => {
    overlay.classList.remove("is-open");
    setTimeout(() => {
      lbImg.src = "";
    }, 300);
    unlockScroll();
    history.replaceState(null, "", window.location.pathname);
  };

  const navigate = (dir) => {
    currentIndex = (currentIndex + dir + gallery.length) % gallery.length;
    lbImg.style.opacity = "0";
    setTimeout(() => showImage(currentIndex), 150);
  };

  // Ouvrir au click sur un trigger
  triggers.forEach((el, i) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      open(i);
    });
  });

  // Click sur l'overlay = fermer
  overlay.addEventListener("click", close);

  // Clavier
  document.addEventListener("keydown", (e) => {
    if (!overlay.classList.contains("is-open")) return;
    if (e.key === "Escape") close();
    if (e.key === "ArrowLeft") navigate(-1);
    if (e.key === "ArrowRight") navigate(1);
  });

  // Curseur custom au dessus
  overlay.addEventListener("mousemove", (e) => {
    gsap.set(cursor, { x: e.clientX, y: e.clientY });
  });
};

initLightbox();

// ———————————————————————————
// ABOUT ME RANDOM QUOTE
// ———————————————————————————
const quotes = [
  "Coffee before Photoshop.",
  "Probably editing a video right now.",
  "Designing with music way too loud.",
  "Am I doing this right?",
  "Oh hey, that looks nice.",
  "MEDIA OFFLINE !? WHAT ?!",
  "Still moving things by 1px.",
];

const quote = document.querySelector(".random-quote");

let quoteIndex = 0;
let charIndex = 0;
let deleting = false;

const typingSpeed = 75;
const deletingSpeed = 35;
const pauseAfterTyping = 4000;
const pauseAfterDeleting = 500;

function typeWriter() {
  const current = quotes[quoteIndex];

  if (!deleting) {
    quote.textContent = current.substring(0, charIndex);

    charIndex++;

    if (charIndex <= current.length) {
      setTimeout(typeWriter, typingSpeed);
    } else {
      deleting = true;
      setTimeout(typeWriter, pauseAfterTyping);
    }
  } else {
    quote.textContent = current.substring(0, charIndex);

    charIndex--;

    if (charIndex >= 0) {
      setTimeout(typeWriter, deletingSpeed);
    } else {
      deleting = false;
      quoteIndex = (quoteIndex + 1) % quotes.length;
      setTimeout(typeWriter, pauseAfterDeleting);
    }
  }
}

typeWriter();

// ——————————————————————————
//  ABOUT ME IMG
// ——————————————————————————
const image = document.querySelector(".about-image-container img");

let mouseX = 0;
let mouseY = 0;

let currentX = 0;
let currentY = 0;

const maxMove = 30; // max translation in px

window.addEventListener("mousemove", (e) => {
  // Normalize mouse position between -0.5 and 0.5
  mouseX = (e.clientX / window.innerWidth - 0.5) * maxMove * 2;
  mouseY = (e.clientY / window.innerHeight - 0.5) * maxMove * 3;
});

function animate() {
  // Smooth interpolation
  currentX += (mouseX - currentX) * 0.08;
  currentY += (mouseY - currentY) * 0.08;

  image.style.transform = `
    translate(${currentX}px, ${currentY}px)
    scale(1.2)
  `;

  requestAnimationFrame(animate);
}

animate();
